import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Linkedin, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface LinkedInConnectionStatusProps {
  isConnected: boolean;
  onConnectionChange: (connected: boolean) => void;
}

export const LinkedInConnectionStatus = ({ isConnected, onConnectionChange }: LinkedInConnectionStatusProps) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { toast } = useToast();

  const handleConnect = async () => {
    setIsConnecting(true);
    
    try {
      // Simulate OAuth flow - in reality this would redirect to LinkedIn OAuth
      // or use a service like Unipile for LinkedIn API access
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      onConnectionChange(true);
      setShowDialog(false);
      toast({
        title: "LinkedIn Connected",
        description: "Your LinkedIn account has been successfully connected.",
      });
    } catch (error) {
      toast({
        title: "Connection Failed",
        description: "Failed to connect to LinkedIn. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    onConnectionChange(false);
    toast({
      title: "LinkedIn Disconnected",
      description: "Your LinkedIn account has been disconnected.",
    });
  };

  if (isConnected) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Connected
        </Badge>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              Manage
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>LinkedIn Connection</DialogTitle>
              <DialogDescription>
                Manage your LinkedIn account connection and permissions.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Connected Successfully</p>
                  <p className="text-sm text-green-700">Your LinkedIn account is connected and ready to use.</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium">Available Features:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Schedule and publish posts</li>
                  <li>• Send and manage InMails</li>
                  <li>• Track engagement analytics</li>
                  <li>• Automate connection requests</li>
                  <li>• Monitor profile activity</li>
                </ul>
              </div>

              <div className="pt-4 border-t">
                <Button 
                  variant="destructive" 
                  onClick={handleDisconnect}
                  className="w-full"
                >
                  Disconnect LinkedIn Account
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Linkedin className="h-4 w-4 mr-2" />
          Connect LinkedIn
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Linkedin className="h-5 w-5 text-blue-600" />
            Connect LinkedIn Account
          </DialogTitle>
          <DialogDescription>
            Connect your LinkedIn account to access marketing and automation features.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Professional Integration:</strong> We use secure OAuth 2.0 authentication and comply with LinkedIn's API policies.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">What you'll get access to:</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Schedule and publish posts</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Send personalized InMails</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Track engagement analytics</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Automate connection requests</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Monitor profile activity</span>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button 
              onClick={handleConnect} 
              disabled={isConnecting}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isConnecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                <>
                  <Linkedin className="h-4 w-4 mr-2" />
                  Connect with LinkedIn
                </>
              )}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              By connecting, you agree to LinkedIn's terms of service and our privacy policy.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};